import { useQuery } from '@tanstack/react-query';

import api from '@/hooks/useApi';

// Types matching backend NodeResponse (from common/src/types/index.ts)
export interface GeneratedChoice {
  text: string;
  consequenceHint: string;
}

export interface ChildNodeIds {
  '0': string | null;
  '1': string | null;
  '2': string | null;
}

export interface ChildVisited {
  '0': boolean;
  '1': boolean;
  '2': boolean;
}

// Access status for each child choice (token economy)
export type ChildAccessStatus = 'explored' | 'free' | 'locked' | 'insufficient' | 'unavailable';

export interface ChildAccess {
  '0': ChildAccessStatus;
  '1': ChildAccessStatus;
  '2': ChildAccessStatus;
}

export interface NodeData {
  nodeId: string;
  storyId: string;
  text: string;
  choices: GeneratedChoice[];
  chapterNumber: number;
  chapterTitle: string;
  isEnding: boolean;
  childNodeIds: ChildNodeIds;
  childVisited: ChildVisited;
  childAccess?: ChildAccess;
  tokenBalance?: number;
  generationEnabled: boolean;
}

export interface GeneratingResponse {
  message: string;
  retryAfterSeconds: number;
}

// Query keys for cache management
export const nodeKeys = {
  all: ['nodes'] as const,
  details: () => [...nodeKeys.all, 'detail'] as const,
  detail: (id: string) => [...nodeKeys.details(), id] as const,
};

/**
 * Fetch a node by ID
 * Returns node data or generating status
 */
export function useNode(nodeId: string | undefined) {
  return useQuery({
    queryKey: nodeKeys.detail(nodeId ?? ''),
    queryFn: async () => {
      const response = await api.get<NodeData | GeneratingResponse>(`/nodes/${nodeId}`);
      return response.data;
    },
    enabled: !!nodeId,
    // Poll every 5 seconds while generating (refetchInterval returns false to stop polling)
    refetchInterval: (query) => {
      const data = query.state.data;
      if (data && isGenerating(data)) {
        return data.retryAfterSeconds * 1000;
      }
      return false;
    },
    // Don't use stale cache when polling for generation status
    staleTime: 0,
    // Don't retry on 404 (node doesn't exist) or 422 (generation failed)
    retry: (failureCount, error) => {
      const status = (error as { response?: { status: number } })?.response?.status;
      if (status === 404 || status === 422) {
        return false;
      }
      return failureCount < 3;
    },
  });
}

/**
 * Type guard to check if response is generating
 */
export function isGenerating(data: NodeData | GeneratingResponse): data is GeneratingResponse {
  return 'retryAfterSeconds' in data;
}
